import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus, Camera, Download, ShieldCheck } from "lucide-react";
import { Panel, StatCard } from "@/project/components/dashboard-shell";
import { causes, institutions, formatINR, progressPct, sampleDonations } from "@/project/data/sample";
import { ComboBarLineChart, DonutChart, RadialGauge, HBarChart } from "@/project/components/charts";

export const Route = createFileRoute("/institution/")({ component: InstDash });

function InstDash() {
  const inst = institutions[0];
  const myCauses = causes.filter((c) => c.institutionId === inst.id);
  const inr = (v: number) => formatINR(v);
  const trend = ["W1","W2","W3","W4","W5","W6","W7","W8"].map((label, i) => ({
    label,
    donations: [21000, 34000, 28500, 41000, 52000, 47800, 61200, 74500][i],
    donors: [12, 18, 15, 22, 27, 24, 31, 38][i],
  }));
  const causeBars = myCauses.slice(0, 5).map((c) => ({ label: c.title.length > 22 ? c.title.slice(0, 22) + "…" : c.title, value: c.raised }));
  const fundUse = [
    { name: "Fodder", value: 45 },
    { name: "Medical", value: 30 },
    { name: "Shelter", value: 15 },
    { name: "Operations", value: 10 },
  ];
  return (
    <>
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4 rounded-3xl bg-white border border-border/80 p-6 sm:p-8 shadow-xs">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--saffron)]/20 px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-[var(--maroon-deep)] border border-[var(--saffron-deep)]/30">
            <ShieldCheck className="h-3.5 w-3.5 text-[var(--saffron-deep)]" /> Institution Console
          </span>
          <h1 className="mt-3 font-display text-2xl font-extrabold sm:text-3xl text-[var(--maroon-deep)]">Namaste, {inst.name}</h1>
          <p className="mt-1 text-sm text-foreground/70">{inst.city} · Powered by Daan Amrut Foundation</p>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[var(--cream-2)] px-3.5 py-1 text-xs font-semibold text-[var(--maroon-deep)] border border-border/60">
            <ShieldCheck className="h-3.5 w-3.5 text-[var(--saffron-deep)]" /> Verified · 80G eligible
          </div>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <Link to="/institution/causes" className="inline-flex items-center gap-2 rounded-full bg-[var(--maroon)] px-4 py-2.5 text-sm font-bold text-white shadow-xs hover:bg-[var(--maroon-deep)] transition">
            <Plus className="h-4 w-4" /> New cause
          </Link>
          <Link to="/institution/impact" className="inline-flex items-center gap-2 rounded-full border border-border bg-[var(--cream-2)] px-4 py-2.5 text-sm font-semibold text-[var(--maroon-deep)] hover:bg-white transition">
            <Camera className="h-4 w-4" /> Post update
          </Link>
          <Link to="/institution/reports" className="inline-flex items-center gap-2 rounded-full border border-border bg-[var(--cream-2)] px-4 py-2.5 text-sm font-semibold text-[var(--maroon-deep)] hover:bg-white transition">
            <Download className="h-4 w-4" /> Download report
          </Link>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total received" value={formatINR(inst.totalDonations)} tone="primary" />
        <StatCard label="Active causes" value={String(myCauses.length)} tone="warn" />
        <StatCard label="Monthly donations" value={formatINR(184200)} tone="good" />
        <StatCard label="Profile completion" value="92%" hint="Add 2 photos to reach 100%" />
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel title="Donations & donors · last 8 weeks" action={<span className="text-xs text-foreground/70">Weekly</span>}>
            <ComboBarLineChart data={trend} valueFormatter={inr} />
          </Panel>
        </div>
        <Panel title="Fund utilisation">
          <DonutChart data={fundUse} valueFormatter={(v) => `${v}%`} />
        </Panel>
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel title="Cause performance · raised">
            <HBarChart data={causeBars.length ? causeBars : [{ label: "No causes yet", value: 0 }]} valueFormatter={inr} />
          </Panel>
        </div>
        <Panel title="Profile completion">
          <RadialGauge value={92} label="Complete" hint="Add 2 photos to reach 100%" color="var(--success)" />
        </Panel>
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Panel title="Cause performance">
          <ul className="space-y-4">
            {myCauses.map((c) => (
              <li key={c.id}>
                <div className="flex justify-between text-sm"><span className="font-medium">{c.title}</span><span className="text-[var(--maroon)] font-semibold">{progressPct(c)}%</span></div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-[var(--cream-2)]"><div className="h-full" style={{ width: `${progressPct(c)}%`, background: "var(--gradient-warm)" }} /></div>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="Recent donations">
          <ul className="divide-y divide-border text-sm">
            {sampleDonations.slice(0, 5).map((d) => (
              <li key={d.id} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--accent)] text-xs font-bold text-[var(--maroon-deep)]">{d.donor.charAt(0)}</span>
                  <div><p className="text-sm">{d.donor}</p><p className="text-xs text-foreground/70">{d.date}</p></div>
                </div>
                <span className="font-semibold text-[var(--maroon)]">{formatINR(d.amount)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </>
  );
}